import { FC, ReactNode, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { Link as RouterLink } from 'react-router-dom'
import { lgpdService, ConsentimentoResponse } from '@/services/lgpdService'
import { authStore } from '@/store/authStore'

/**
 * Gate de consentimento LGPD: ao montar, verifica se o usuário autenticado
 * já aceitou a versão vigente da Política de Privacidade e Termos. Se não,
 * exibe modal bloqueante até o aceite.
 */
export const ConsentimentoGate: FC<{ children: ReactNode }> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar()
  const isAuthenticated = authStore((s) => s.isAuthenticated)

  const [carregando, setCarregando] = useState(true)
  const [aceitoOk, setAceitoOk] = useState(true)
  const [versao, setVersao] = useState<string>('')
  const [marcou, setMarcou] = useState(false)
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) {
      setCarregando(false)
      setAceitoOk(true)
      return
    }
    let cancelado = false
    setCarregando(true)
    Promise.all([lgpdService.versao(), lgpdService.meuConsentimento()])
      .then(([v, c]: [string, ConsentimentoResponse]) => {
        if (cancelado) return
        setVersao(v)
        setAceitoOk(!!c?.aceito && c.versao === v)
      })
      .catch(() => {
        // Se a chamada falhar (ex.: backend offline), não trava o usuário — segue.
        setAceitoOk(true)
      })
      .finally(() => {
        if (!cancelado) setCarregando(false)
      })
    return () => {
      cancelado = true
    }
  }, [isAuthenticated])

  const aceitar = async () => {
    if (!marcou) return
    setEnviando(true)
    try {
      await lgpdService.aceitar(versao)
      setAceitoOk(true)
      enqueueSnackbar('Aceite registrado.', { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(
        e instanceof Error ? e.message : 'Falha ao registrar aceite',
        { variant: 'error' }
      )
    } finally {
      setEnviando(false)
    }
  }

  if (carregando) {
    // Não bloqueia render durante load inicial — só atrasaria o app
    return <>{children}</>
  }

  return (
    <>
      {children}
      <Dialog open={!aceitoOk} disableEscapeKeyDown maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0, fontWeight: 700 }}>
          Atualização da Política de Privacidade
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Para continuar utilizando o portal, precisamos do seu aceite na versão atualizada
              da nossa Política de Privacidade e Termos de Uso. Versão vigente:{' '}
              <strong>{versao || '—'}</strong>.
            </Alert>
            <Typography variant="body2">
              Recomendamos a leitura completa antes de aceitar:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Link component={RouterLink} to="/privacidade" target="_blank" rel="noreferrer">
                Política de Privacidade ↗
              </Link>
              <Link component={RouterLink} to="/termos" target="_blank" rel="noreferrer">
                Termos de Uso ↗
              </Link>
            </Box>
            <FormControlLabel
              control={<Checkbox checked={marcou} onChange={(_, v) => setMarcou(v)} />}
              label={
                <Typography variant="body2">
                  Li e concordo com a Política de Privacidade e os Termos de Uso versão {versao}.
                </Typography>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            disabled={!marcou || enviando}
            onClick={aceitar}
            startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {enviando ? 'Enviando…' : 'Aceitar e continuar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
